<script setup>
import Badge from "@Components/Badge.vue";
import { ChevronDownIcon, ChevronUpIcon, Trash2Icon } from "lucide-vue-next";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import PreviewInput from "./PreviewInput.vue";
import { FormListbox, FormSwitch } from "@Components/Form";
import { useStore } from "@/Stores/templates";

const project = defineModel("project");

defineProps({
    categories: {
        type: Array,
        default: [],
    },
    profiles: {
        type: Array,
        default: [],
    },
});

defineEmits(["deleteProject"]);

const { t: __ } = useI18n();
const store = useStore();

const showDetails = ref(false);

const type = computed(() => {
    switch (project.value.type) {
        case "post":
            return __("Post");
        case "news":
            return __("Articles d'actualités");
        case "rss":
            return __("RSS");
        case "sitemap":
            return __("Sitemap");
        case "url":
            return __("URL");
        case "siloing":
            return __("Architecture de pages");
    }

    return project.value.type;
});

const templates = computed(() => [{ name: __("Template automatique"), id: null }, ...store.localTemplates.map((t) => ({ name: t.name, id: `tpl_${t.id}` }))]);
</script>
<template>
    <div>
        <div class="flex items-center gap-1.5">
            <Badge class="flex-none text-xs">{{ type }}</Badge>
            <div class="grid flex-1 grid-cols-6 gap-1.5">
                <div class="col-span-full">
                    <PreviewInput :placeholder="__('Nom du projet')" type="text" v-model="project.name" />
                </div>
            </div>
            <Trash2Icon
                @click="$emit('deleteProject')"
                class="size-4 flex-none text-gray-600 cursor-pointer dark:text-gray-400 dark:hover:text-white hover:text-gray-900" />
            <component
                :is="showDetails ? ChevronUpIcon : ChevronDownIcon"
                @click="showDetails = !showDetails"
                class="size-4 flex-none text-gray-600 cursor-pointer dark:text-gray-400 dark:hover:text-white hover:text-gray-900" />
        </div>

        <div v-if="showDetails" class="px-3 py-2">
            <div class="grid grid-cols-6 gap-1.5">
                <template v-if="project.type === 'post'">
                    <template v-if="project.titles && project.titles.length > 0">
                        <div class="col-span-full" v-for="(title, titleIdx) in project.titles" :key="titleIdx">
                            <PreviewInput :placeholder="__('Titre')" type="text" v-model="project.titles[titleIdx]" />
                        </div>
                    </template>
                    <template v-else>
                        <div class="col-span-4">
                            <PreviewInput :placeholder="__('Thème')" type="text" v-model="project.theme" />
                        </div>
                        <div class="col-span-2">
                            <PreviewInput
                                :placeholder="__('Nombre d\'articles à générer')"
                                type="number"
                                v-model="project.number_of_posts_to_generate"
                                :min="1" />
                        </div>
                    </template>
                </template>
                <template v-else-if="project.type === 'news'">
                    <div class="col-span-4">
                        <PreviewInput :placeholder="__('Thème')" type="text" v-model="project.theme" />
                    </div>
                    <div class="col-span-2">
                        <PreviewInput
                            :placeholder="__('Nombre d\'articles à générer par jour')"
                            type="number"
                            v-model="project.number_of_posts_to_generate"
                            :min="1" />
                    </div>
                </template>
                <template v-else-if="project.type === 'url'">
                    <template v-if="project.urls && project.urls.length > 0">
                        <div class="col-span-full" v-for="(url, urlIdx) in project.urls" :key="urlIdx">
                            <PreviewInput :placeholder="__('Url')" type="text" v-model="project.urls[urlIdx]" />
                        </div>
                    </template>
                </template>
                <template v-else-if="project.type === 'sitemap'">
                    <div class="col-span-full">
                        <PreviewInput :placeholder="__('URL du sitemap')" type="text" v-model="project.sitemap_url" />
                    </div>
                </template>
                <template v-else-if="project.type === 'rss'">
                    <div class="col-span-full">
                        <PreviewInput :placeholder="__('URL du RSS')" type="text" v-model="project.rss_url" />
                    </div>
                </template>
                <template v-else-if="project.type === 'siloing'">
                    <div class="col-span-full">
                        <PreviewInput :placeholder="__('Thème')" type="text" v-model="project.theme" />
                    </div>
                </template>
                <div class="col-span-3">
                    <FormListbox v-model="project.profile_id" :items="profiles" />
                </div>
                <div class="col-span-3" v-if="project.type !== 'siloing'">
                    <FormListbox v-model="project.category_id" :items="categories" />
                </div>
                <div class="col-span-full">
                    <FormListbox v-model="project.template_id" :items="templates" required />
                </div>
                <div class="col-span-full" v-if="project.template_id === null">
                    <textarea
                        :placeholder="__('Instructions personnalisées pour le template automatique')"
                        :rows="2"
                        v-model="project.automatic_template_custom_instructions"
                        class="w-full block px-0 py-0.5 text-xs leading-5 text-gray-900 bg-transparent border-t-0 border-r-0 border-b border-l-0 border-gray-300 ring-0 dark:text-white dark:border-gray-800 dark:focus:border-gray-700 dark:placeholder:text-gray-400 focus:border-gray-950 placeholder:text-gray-400" />
                </div>
                <div class="col-span-3">
                    <FormSwitch size="small" v-model="project.generate_seo_title" :label="__('Générer un titre SEO')" />
                </div>
                <div class="col-span-3">
                    <FormSwitch size="small" v-model="project.generate_seo_description" :label="__('Générer une description SEO')" />
                </div>
            </div>
        </div>
    </div>
</template>
